import React, { Component } from 'react'
import { RouteComponentProps } from 'react-router-dom'
import { appRef } from '../../../app'
import { HiSwitchVertical, HiHashtag, HiChip, HiOutlineMenu, HiDotsHorizontal } from 'react-icons/hi'
import classnames from 'classnames'
import { onCustomerConnect, clearCustomerConnect, IListenerUID, CustomerState } from '../../../wsclient/handlers/customertraffic.handler'
import { IClientData, getClientById } from '../../../api/clients'
import { toast } from 'react-toastify'
import { getString } from '../../../locale'
import { ChipModel } from '../../../constants/chip-model.enum'
import { IStaticTraits } from '../../../api/0x0002/statictraits'

import { getMachine, IMachine } from '../../../api/0x0002/machine'
import { CoolingUnitType, getClientStaticTraits } from '../../../api/0x0002/statictraits'
import { isSoftwareOutdated } from '../../../api/0x0002/software-versions'
import FragmentedSystemInfoTab from '../0x0002/fragmented-systeminfo.tab'
import FragmentedSettingsTab from '../0x0002/fragmented-settings.tab'
import LiveLogsTab from '../0x0005/live-logs.tab'
import DateTimeTab from '../0x0002/datetime.tab'
import CustomerDataTab from '../0x0001/customerdata.tab'
import SystemActionsTab from '../0x0002/systemactions.tab'
import MonitoringTab from '../0x0002/monitoring.tab'
import OtaTab from '../0x0001/ota.tab'
import FlashDeviceTab from '../0x0001/flashdevice.tab'

import WifiTab from './wifi.tab'

type Props = {
    client: IClientData
}

type State = {
    showMobileMenu: boolean
    tab: Tab
    // staticTraits?: IStaticTraits
    staticTraits?: IStaticTraits
    outdated: boolean
    newVersion?: string
    showMoreTraits: boolean
}

enum Tab {
    SystemInfo,
    LiveLogs,
    Settings,
    Wifi,
    DateTime,
    CustomerData,
    SystemActions,
    Monitoring,
    Ota,
    FlashDevice
}

const TabSlug: { [key in Tab]: string } = {
    [Tab.SystemInfo]: 'system-info',
    [Tab.LiveLogs]: 'live-logs',
    [Tab.Settings]: 'settings',
    [Tab.Wifi]: 'wifi',
    [Tab.DateTime]: 'datetime',
    [Tab.CustomerData]: 'customer-data',
    [Tab.SystemActions]: 'system-actions',
    [Tab.Monitoring]: 'monitoring',
    [Tab.Ota]: 'ota',
    [Tab.FlashDevice]: 'flash-device',
}

const SlugToTab: { [key: string]: Tab } = {
    'system-info': Tab.SystemInfo,
    'logs': Tab.LiveLogs,
    'live-logs': Tab.LiveLogs,
    'settings': Tab.Settings,
    'wifi': Tab.Wifi,
    'wifi-config': Tab.Wifi,
    'datetime': Tab.DateTime,
    'customer-data': Tab.CustomerData,
    'system-actions': Tab.SystemActions,
    'monitoring': Tab.Monitoring,
    'ota': Tab.Ota,
    'flash-device': Tab.FlashDevice,
}

export class Client extends Component<Props & RouteComponentProps> {
    state: State = {
        showMobileMenu: false,
        tab: process.env.NODE_ENV === 'development' ? Tab.SystemInfo : Tab.SystemInfo,
        outdated: false,
        newVersion: undefined,
        showMoreTraits: false,
    }

    machine?: IMachine

    listenerId?: IListenerUID

    componentDidMount = async () => {
        console.log('Loading 0x0006 Model Data (LTCB220)')

        const { client } = this.props

        const urlParams = new URLSearchParams(this.props.location.search)
        const tabSlug = urlParams.get('tab')
        if (tabSlug && SlugToTab[tabSlug] !== undefined) {
            this.setState({ tab: SlugToTab[tabSlug] })
        }

        const [clientResult, machineResult, staticTraitsResult] = await Promise.allSettled([
            getClientById(client.id),
            getMachine(client.id),
            getClientStaticTraits(client.id),
        ])

        if (clientResult.status === 'fulfilled') {
            const client = clientResult.value.data
            console.log('client', client)
            this.setState({ client })
        } else {
            console.error('Failed to get client data', clientResult.reason)
        }

        if (machineResult.status === 'fulfilled') {
            const machine = machineResult.value.data
            console.log('machine', machine)
            this.machine = machine
        } else {
            console.error('Failed to get machine data', machineResult.reason)
        }

        if (staticTraitsResult.status === 'fulfilled') {
            const staticTraits = staticTraitsResult.value.data
            console.log('staticTraits', staticTraits)
            this.setState({ staticTraits })

            try {
                const versionCheck = await isSoftwareOutdated(staticTraits.version, staticTraits.model)
                this.setState({
                    outdated: versionCheck.data.outdated,
                    newVersion: versionCheck.data.softwareVersion?.version,
                })
            } catch (e) {
                toast.error('Failed to check software version', e)
            }
        } else {
            console.error('Failed to get static traits', staticTraitsResult.reason)
        }

        appRef.setHeader(
            <HiSwitchVertical className="me-2" size={28} />,
            () => `${this.machine?.name || getString('pagelabels.client')} ${client.id}`,
        )
        this.listenerId = onCustomerConnect(this.handleClientConnect.bind(this))
    }

    componentWillUnmount = () => {
        clearCustomerConnect(this.listenerId!)
    }

    handleClientConnect(clientConnect: IClientData, state: CustomerState) {
        // handled globally in App to keep disconnect behavior consistent
    }

    render() {
        return (
            <div className="flex flex-col lg:grid grid-cols-3 gap-5 lg:gap-10">
                <div>{this.renderSidebar()}</div>
                <div className="col-span-2">{this.renderContent()}</div>
            </div>
        )
    }

    renderSidebar() {
        return (
            <>
                <div className="hidden lg:block sticky top-5">
                    {this.renderSideNav()}
                    {this.renderStaticTraits()}
                </div>
                <div className="w-full flex lg:hidden justify-end">
                    <button
                        title="menu"
                        className="text-black dark:text-white hover:text-gray-300 focus:outline-none focus:text-gray-300"
                        onClick={() => {
                            this.setState({ showMobileMenu: !this.state.showMobileMenu })
                        }}
                    >
                        <HiOutlineMenu className="h-6 w-6" />
                    </button>
                </div>
                {this.state.showMobileMenu && (
                    <div className="mt-5">
                        {this.renderSideNav()}
                        {this.renderStaticTraits()}
                    </div>
                )}
            </>
        )
    }

    renderStaticTraits() {
        const { showMoreTraits } = this.state
        return (
            <ul className="bg-white dark:bg-gray-800 rounded-xl shadow-md">
                {this.renderStaticTrait(getString('static_traits.version'), HiChip, this.state.staticTraits?.version)}
                {this.renderStaticTrait(getString('static_traits.idf_ver'), HiChip, this.state.staticTraits?.idfVer)}
                {this.renderStaticTrait(getString('static_traits.cores'), HiChip, this.state.staticTraits?.cores)}
                {this.renderStaticTrait(
                    getString('static_traits.model'),
                    HiChip,
                    this.state.staticTraits?.model ? ChipModel[this.state.staticTraits.model] : undefined,
                )}
                {this.renderStaticTrait(getString('static_traits.revision'), HiChip, this.state.staticTraits?.revision)}

                {this.renderStaticTrait(getString('build_flags.partname'), HiChip, this.state.staticTraits?.buildFlags.PARTNAME)}
                {!showMoreTraits ? (
                    <li
                        className="flex items-center justify-center p-4 cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors first:rounded-t-xl last:rounded-b-xl"
                        onClick={() => this.setState({ showMoreTraits: true })}
                    >
                        <HiDotsHorizontal className="me-5" color="#3B82F6" />
                    </li>
                ) : (
                    <>
                        {this.renderStaticTrait(
                            getString('build_flags.soda_unit'),
                            HiChip,
                            this.state.staticTraits?.buildFlags.INCLUDE_SODA_UNIT ? getString('common.included') : getString('common.excluded'),
                        )}
                        {this.renderStaticTrait(
                            getString('build_flags.cooling_unit'),
                            HiChip,
                            this.state.staticTraits ? CoolingUnitType[this.state.staticTraits.buildFlags.INCLUDE_COOLING_UNIT] : undefined,
                        )}
                        {this.renderStaticTrait(
                            getString('build_flags.ambient_unit'),
                            HiChip,
                            this.state.staticTraits?.buildFlags.INCLUDE_AMBIENT_UNIT ? getString('common.included') : getString('common.excluded'),
                        )}
                        {this.renderStaticTrait(
                            getString('build_flags.circulation_pump_unit'),
                            HiChip,
                            this.state.staticTraits?.buildFlags.INCLUDE_CIRCULATION_PUMP_UNIT ? getString('common.included') : getString('common.excluded'),
                        )}
                        {this.renderStaticTrait(
                            getString('build_flags.heating_unit'),
                            HiChip,
                            this.state.staticTraits?.buildFlags.INCLUDE_HEATING_UNIT ? getString('common.included') : getString('common.excluded'),
                        )}
                        {this.renderStaticTrait(
                            getString('build_flags.bluetooth'),
                            HiChip,
                            this.state.staticTraits?.buildFlags.INCLUDE_BLUETOOTH ? getString('common.included') : getString('common.excluded'),
                        )}
                        {this.renderStaticTrait(
                            getString('build_flags.wifi'),
                            HiChip,
                            this.state.staticTraits?.buildFlags.INCLUDE_WIFI ? getString('common.included') : getString('common.excluded'),
                        )}
                        {this.renderStaticTrait(
                            getString('build_flags.rel_sols'),
                            HiChip,
                            <div className="flex flex-row">
                                {this.state.staticTraits?.buildFlags.INCLUDE_RELSOL_1 && <div className="ms-1">1</div>}
                                {this.state.staticTraits?.buildFlags.INCLUDE_RELSOL_2 && <div className="ms-1">2</div>}
                                {this.state.staticTraits?.buildFlags.INCLUDE_RELSOL_3 && <div className="ms-1">3</div>}
                                {this.state.staticTraits?.buildFlags.INCLUDE_RELSOL_4 && <div className="ms-1">4</div>}
                                {this.state.staticTraits?.buildFlags.INCLUDE_RELSOL_5 && <div className="ms-1">5</div>}
                                {this.state.staticTraits?.buildFlags.INCLUDE_RELSOL_6 && <div className="ms-1">6</div>}
                                {this.state.staticTraits?.buildFlags.INCLUDE_RELSOL_7 && <div className="ms-1">7</div>}
                                {this.state.staticTraits?.buildFlags.INCLUDE_RELSOL_8 && <div className="ms-1">8</div>}
                            </div>,
                        )}
                        {this.renderStaticTrait(
                            getString('build_flags.serial'),
                            HiChip,
                            this.state.staticTraits?.buildFlags.INCLUDE_SERIAL ? getString('common.included') : getString('common.excluded'),
                        )}
                        {this.renderStaticTrait(
                            getString('build_flags.keypad'),
                            HiChip,
                            this.state.staticTraits?.buildFlags.INCLUDE_KEYPAD ? getString('common.included') : getString('common.excluded'),
                        )}
                    </>
                )
                }
            </ul>
        )
    }

    renderStaticTrait(label: string, IconComp: React.ElementType, value?: string | number | JSX.Element) {
        return (
            <li className="flex items-center p-4 dark:text-white first:rounded-t-xl last:rounded-b-xl text-sm">
                <div className="flex items-center">
                    <IconComp className="me-5" />
                    {label}
                </div>
                <div className="flex-grow" />
                <div>{value}</div>
            </li>
        )
    }

    renderSideNav() {
        const { client } = this.props
        return (
            <ul className="bg-white dark:bg-gray-800 rounded-xl shadow-md mb-5">
                {this.renderSidebarItem(Tab.SystemInfo, getString('client.system_info'))}
                {this.renderSidebarItem(Tab.LiveLogs, getString('general_settings.live_logs_title'))}
                {this.renderSidebarItem(Tab.Settings, getString('client.settings'))}
                {this.renderSidebarItem(Tab.Wifi, getString('client.wifi_config'))}
                {this.renderSidebarItem(Tab.DateTime, getString('client.datetime'))}
                {this.renderSidebarItem(Tab.CustomerData, getString('client.customer_data'))}
                {this.renderSidebarItem(Tab.SystemActions, getString('client.system_actions'))}
                {this.renderSidebarItem(Tab.Monitoring, getString('client.monitoring'))}
                {this.renderSidebarItem(Tab.Ota, getString('client.ota'))}
                {client?.phyDebugger && this.renderSidebarItem(Tab.FlashDevice, getString('client.flash_device'))}
            </ul>
        )
    }

    renderSidebarItem(tabId: number, label: string) {
        const { tab } = this.state
        return (
            <li
                className={classnames(
                    tab === tabId && 'bg-gray-200 dark:bg-gray-700',
                    'flex items-center p-4 dark:text-white hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors first:rounded-t-xl last:rounded-b-xl cursor-pointer',
                )}
                onClick={() => {
                    this.setState({ tab: tabId, showMobileMenu: false })
                    const tabSlug = TabSlug[tabId as Tab]
                    const newUrl = `${this.props.location.pathname}?tab=${tabSlug}`
                    this.props.history.replace(newUrl)
                }}
            >
                <HiHashtag className="me-5" />
                {label}
            </li>
        )
    }

    renderContent() {
        const { client } = this.props
        const { tab } = this.state
        if (!client) return undefined

        switch (tab) {
            case Tab.SystemInfo:
                return <FragmentedSystemInfoTab clientId={client.id} />
            case Tab.LiveLogs:
                return <LiveLogsTab clientId={client.id} />
            case Tab.Settings:
                return <FragmentedSettingsTab clientId={client.id} />
            case Tab.Wifi:
                return <WifiTab clientId={client.id} firmwareVersion={this.state.staticTraits?.version} />
            case Tab.DateTime:
                return <DateTimeTab clientId={client.id} />
            case Tab.CustomerData:
                return <CustomerDataTab clientId={client.id} />
            case Tab.SystemActions:
                return <SystemActionsTab clientId={client.id} />
            case Tab.Monitoring:
                return <MonitoringTab clientId={client.id} />
            case Tab.Ota:
                return <OtaTab clientId={client.id} />
            case Tab.FlashDevice:
                return <FlashDeviceTab clientId={client.id} />
        }
    }
}
